import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Save, Trash2, Play, Download, Upload, Clock, Cloud, HardDrive, Loader2, Share2 } from 'lucide-react';
import { useGameStore } from '@/store/gameStore';
import { exportPGN, importPGN } from '@/utils/storage';
import { saveGameCloud, getSavedGamesCloud, deleteSavedGameCloud } from '@/utils/cloudStorage';
import { useAuth } from '@/contexts/AuthContext';
import ShareDialog from './ShareDialog';

const SaveLoadDialog = ({ isOpen, onClose, mode = 'save' }) => {
  const [gameName, setGameName] = useState('');
  const [savedGames, setSavedGames] = useState([]);
  const [pgnInput, setPgnInput] = useState('');
  const [showPgnImport, setShowPgnImport] = useState(false);
  const [importError, setImportError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shareGame, setShareGame] = useState(null);
  const { getGameData, loadGame, playerColor, history, boardTheme, gameStatus } = useGameStore();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isOpen) {
      loadSavedGames();
      setShowPgnImport(false);
      setPgnInput('');
      setImportError('');
    }
  }, [isOpen, mode]);

  const loadSavedGames = async () => {
    setIsLoading(true);
    try {
      const games = await getSavedGamesCloud();
      setSavedGames(games);
    } catch (error) {
      console.error('Failed to load games:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!gameName.trim()) return;
    
    setIsSaving(true);
    try {
      const gameData = getGameData();
      await saveGameCloud(gameName.trim(), gameData);
      setGameName('');
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoad = (game) => {
    loadGame(game);
    onClose();
  };

  const handleDelete = async (id) => {
    await deleteSavedGameCloud(id);
    loadSavedGames();
  };

  const handleExportPGN = () => {
    // Determine result
    let result = '*';
    if (gameStatus === 'checkmate') {
      result = playerColor === 'white' ? '1-0' : '0-1';
    } else if (gameStatus === 'draw' || gameStatus === 'stalemate') {
      result = '1/2-1/2';
    }
    
    const pgn = exportPGN(history, result, playerColor, new Date());
    const blob = new Blob([pgn], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chess_game_${new Date().toISOString().split('T')[0]}.pgn`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportPGN = () => {
    if (!pgnInput.trim()) {
      setImportError('Please paste a PGN game');
      return;
    }
    
    const moves = importPGN(pgnInput);
    if (!moves || moves.length === 0) {
      setImportError('Could not parse PGN. Please check the format.');
      return;
    }
    
    // Load the game
    loadGame({ history: moves, playerColor: 'white' });
    onClose();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getResultBadge = (result) => {
    if (result === 'win') return { text: 'Won', color: 'text-green-400 bg-green-500/20' };
    if (result === 'loss') return { text: 'Lost', color: 'text-red-400 bg-red-500/20' };
    if (result === 'draw') return { text: 'Draw', color: 'text-gray-400 bg-gray-500/20' };
    return { text: 'In Progress', color: 'text-cyan-400 bg-cyan-500/20' };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`
          ${boardTheme === 'cosmic'
            ? 'bg-slate-900 border-purple-500/30 text-white'
            : 'bg-amber-50 border-amber-300 text-gray-900'
          }
          max-w-md
        `}
        data-testid="save-load-dialog"
      >
        <DialogHeader>
          <DialogTitle className={boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-amber-800'}>
            {mode === 'save' ? 'Save Game' : 'Load Game'}
          </DialogTitle>
        </DialogHeader>

        {mode === 'save' ? (
          <div className="space-y-4">
            <div>
              <label className={`text-sm mb-2 block ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
                Game Name
              </label>
              <Input
                data-testid="game-name-input"
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                placeholder="Enter a name for your game..."
                className={`
                  ${boardTheme === 'cosmic'
                    ? 'bg-slate-800 border-purple-500/30 text-white'
                    : 'bg-white border-amber-300'
                  }
                `}
              />
            </div>
            
            <div className={`
              p-3 rounded-lg
              ${boardTheme === 'cosmic' ? 'bg-slate-800/50' : 'bg-amber-100'}
            `}>
              <div className="flex justify-between text-sm mb-2">
                <span className={boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}>Moves</span>
                <span className="font-mono">{history.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}>Playing as</span>
                <span className="capitalize">{playerColor}</span>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={handleExportPGN}
              disabled={history.length === 0}
              className={`w-full ${
                boardTheme === 'cosmic'
                  ? 'bg-purple-900/30 border-purple-500/30 text-purple-400 hover:bg-purple-900/50'
                  : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200'
              }`}
            >
              <Download className="w-4 h-4 mr-2" />
              Export as PGN File
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* PGN Import Section */}
            {showPgnImport ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className={`text-sm font-medium ${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Paste PGN
                  </label>
                  <button 
                    onClick={() => setShowPgnImport(false)}
                    className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    Cancel
                  </button>
                </div>
                <textarea
                  value={pgnInput}
                  onChange={(e) => { setPgnInput(e.target.value); setImportError(''); }}
                  placeholder="Paste PGN here...&#10;&#10;Example:&#10;1. e4 e5 2. Nf3 Nc6..."
                  className={`
                    w-full h-32 p-3 rounded-lg text-sm font-mono resize-none
                    ${boardTheme === 'cosmic'
                      ? 'bg-slate-800 border border-purple-500/30 text-white placeholder-gray-600'
                      : 'bg-white border border-amber-300 text-gray-900 placeholder-gray-400'
                    }
                  `}
                />
                {importError && (
                  <p className="text-xs text-red-400">{importError}</p>
                )}
                <Button
                  onClick={handleImportPGN}
                  className={`w-full ${
                    boardTheme === 'cosmic'
                      ? 'bg-cyan-600 hover:bg-cyan-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                  } text-white`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Game
                </Button>
              </div>
            ) : (
              <>
                {/* Import PGN Button */}
                <Button
                  variant="outline"
                  onClick={() => setShowPgnImport(true)}
                  className={`w-full ${
                    boardTheme === 'cosmic'
                      ? 'bg-slate-800 border-purple-500/30 text-purple-400 hover:bg-purple-900/30'
                      : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-100'
                  }`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import from PGN
                </Button>
                
                {/* Saved Games List */}
                <div className="flex items-center justify-between">
                  <div className={`text-xs font-medium uppercase tracking-wider ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`}>
                    Saved Games ({savedGames.length})
                  </div>
                  {isAuthenticated && (
                    <div className={`flex items-center gap-1 text-xs ${boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-blue-600'}`}>
                      <Cloud className="w-3 h-3" />
                      Cloud Sync
                    </div>
                  )}
                </div>
                
                {isLoading ? (
                  <div className={`
                    text-center py-8 rounded-lg
                    ${boardTheme === 'cosmic' ? 'bg-slate-800/50 text-gray-400' : 'bg-amber-100 text-gray-500'}
                  `}>
                    <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
                    <p>Loading saved games...</p>
                  </div>
                ) : savedGames.length === 0 ? (
                  <div className={`
                    text-center py-8 rounded-lg
                    ${boardTheme === 'cosmic' ? 'bg-slate-800/50 text-gray-500' : 'bg-amber-100 text-gray-400'}
                  `}>
                    <Save className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No saved games yet</p>
                    <p className="text-xs mt-1">Save a game to see it here</p>
                  </div>
                ) : (
                  <ScrollArea className="h-64">
                    <div className="space-y-2 pr-2">
                      {savedGames.map((game) => {
                        const result = getResultBadge(game.result);
                        return (
                          <div 
                            key={game.id}
                            className={`
                              p-3 rounded-lg transition-all
                              ${boardTheme === 'cosmic'
                                ? 'bg-slate-800 border border-purple-500/20 hover:border-cyan-500/40'
                                : 'bg-white border border-amber-200 hover:border-amber-400 hover:shadow-sm'
                              }
                            `}
                            data-testid={`saved-game-${game.id}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium truncate">{game.name}</p>
                                  {game.isCloud ? (
                                    <Cloud className={`w-3.5 h-3.5 flex-shrink-0 ${boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-blue-500'}`} title="Synced to cloud" />
                                  ) : (
                                    <HardDrive className={`w-3.5 h-3.5 flex-shrink-0 ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`} title="Saved locally" />
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${result.color}`}>
                                    {result.text}
                                  </span>
                                  <span className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {game.history?.length || 0} moves
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className={`text-xs flex items-center gap-1 ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`}>
                                <Clock className="w-3 h-3" />
                                {formatDate(game.savedAt)}
                              </span>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setShareGame(game)}
                                  className={`
                                    h-7 px-2
                                    ${boardTheme === 'cosmic' 
                                      ? 'text-cyan-400 hover:bg-cyan-900/30' 
                                      : 'text-blue-600 hover:bg-blue-100'
                                    }
                                  `}
                                  title="Share replay"
                                >
                                  <Share2 className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleLoad(game)}
                                  className={`
                                    h-7 px-3
                                    ${boardTheme === 'cosmic' 
                                      ? 'bg-cyan-600 hover:bg-cyan-700 text-white' 
                                      : 'bg-green-600 hover:bg-green-700 text-white'
                                    }
                                  `}
                                >
                                  <Play className="w-3 h-3 mr-1" />
                                  Load
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(game.id)}
                                  className={`
                                    h-7 w-7 p-0
                                    ${boardTheme === 'cosmic' 
                                      ? 'text-red-400 hover:bg-red-900/30' 
                                      : 'text-red-600 hover:bg-red-100'
                                    }
                                  `}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className={boardTheme === 'cosmic' ? 'bg-slate-800 border-slate-600' : ''}
          >
            Cancel
          </Button>
          {mode === 'save' && (
            <Button
              data-testid="confirm-save-btn"
              onClick={handleSave}
              disabled={!gameName.trim() || isSaving}
              className={`
                ${boardTheme === 'cosmic'
                  ? 'bg-cyan-600 hover:bg-cyan-700'
                  : 'bg-amber-600 hover:bg-amber-700'
                }
                text-white
              `}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : isAuthenticated ? (
                <Cloud className="w-4 h-4 mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isSaving ? 'Saving...' : isAuthenticated ? 'Save to Cloud' : 'Save Locally'}
            </Button>
          )}
        </DialogFooter>
        
        {/* Share Dialog for saved games */}
        {shareGame && (
          <ShareDialog 
            isOpen={!!shareGame} 
            onClose={() => setShareGame(null)}
            gameData={{
              name: shareGame.name,
              fen: shareGame.fen,
              history: shareGame.history,
              playerColor: shareGame.playerColor,
              difficulty: shareGame.aiDifficulty,
              result: shareGame.result,
              moveAnalysis: shareGame.moveAnalysis
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SaveLoadDialog;
